/**
 * Stores: everything that takes stock OUT of the store's bins, after seedtrac STORES' Transactions
 * menu, plus the two in-store moves (SLOC Updation, Cycle Inventory) and the e-Indent a plant raises
 * to ask the store for items.
 *
 * Outbound kinds share one engine: a header, then items, each picked from the bins where it sits
 * ("Existing SLOC → Issue → Balance"). Draft → Preview → Final Submit (number minted, stock posted);
 * cancel reverses it. Those that leave the plant go out on the guard's Gate Pass Out.
 */
import { toDecimal, type DecimalInput } from "./decimal";
import {
  missingDispatchTransport,
  type DispatchTransport,
} from "./dispatch-stock-transfer";
import type { StoresCondition } from "./stores";

export const STORES_OUTBOUND_KINDS = [
  "issue_eindent",
  "issue_physical",
  "issue_stock_transfer",
  "return_vendor",
  "captive",
  "discard",
] as const;
export type StoresOutboundKind = (typeof STORES_OUTBOUND_KINDS)[number];

/** Who the stock goes to. */
export type StoresOutboundParty =
  | "indent" // an e-Indent raised in NexSeed
  | "physical_indent" // a paper indent, typed in
  | "plant" // another of our plants
  | "vendor" // a Stores vendor
  | "party_or_text" // a Stores party, or a name and address typed in (Captive: Select / Fill)
  | "text"; // a name and address typed in (Discard)

export interface StoresOutboundConfig {
  kind: StoresOutboundKind;
  label: string;
  title: string;
  prefix: string;
  printCode: string;
  printTitle: string;
  party: StoresOutboundParty;
  /** Which stock it draws: issues take Good, a discard takes Damage, a vendor return either. */
  conditions: readonly StoresCondition[];
  /** Mode of Transit: always asked, asked only when "Applicable", or not at all. */
  transit: "required" | "optional" | "none";
  /** Returnable / Not Returnable. */
  hasReturnStatus: boolean;
  /** The reference number box: STR Ref. No., Party DC Ref. No., Discard Instruction Ref. No. */
  refLabel: string | null;
  refRequired: boolean;
  /** Leaves the plant, so the guard raises a Gate Pass Out (when there is transit at all). */
  gatePass: boolean;
  permission: string;
}

export const STORES_OUTBOUND: Record<StoresOutboundKind, StoresOutboundConfig> =
  {
    issue_eindent: {
      kind: "issue_eindent",
      label: "Internal – e-Indent",
      title: "Internal Issue on e-Indent",
      prefix: "SIE",
      printCode: "EIN",
      printTitle: "Issue Note – e-Indent (EIN)",
      party: "indent",
      conditions: ["good"],
      transit: "none",
      hasReturnStatus: false,
      refLabel: null,
      refRequired: false,
      gatePass: false,
      permission: "stores.issue.internal",
    },
    issue_physical: {
      kind: "issue_physical",
      label: "Internal – Physical Indent",
      title: "Internal Issue on Physical Indent",
      prefix: "SIP",
      printCode: "PIN",
      printTitle: "Issue Note – Physical Indent (PIN)",
      party: "physical_indent",
      conditions: ["good"],
      transit: "none",
      hasReturnStatus: false,
      refLabel: null,
      refRequired: false,
      gatePass: false,
      permission: "stores.issue.internal",
    },
    issue_stock_transfer: {
      kind: "issue_stock_transfer",
      label: "Stock Transfer",
      title: "Issue – Stock Transfer",
      prefix: "SIS",
      printCode: "STON",
      printTitle: "Stock Transfer Out Note (STON)",
      party: "plant",
      conditions: ["good"],
      transit: "required",
      hasReturnStatus: true,
      refLabel: "STR Ref. No.",
      refRequired: false,
      gatePass: true,
      permission: "stores.issue.stock_transfer",
    },
    return_vendor: {
      kind: "return_vendor",
      label: "Return to Vendor",
      title: "Material Return to Vendor",
      prefix: "SRV",
      printCode: "VMRN",
      printTitle: "Vendor Material Return Note (VMRN)",
      party: "vendor",
      conditions: ["good", "damage"],
      transit: "required",
      hasReturnStatus: true,
      refLabel: "Party DC Ref. No.",
      refRequired: false,
      gatePass: true,
      permission: "stores.issue.return_vendor",
    },
    captive: {
      kind: "captive",
      label: "Captive Consumption",
      title: "Captive Consumption",
      prefix: "SCC",
      printCode: "CCN",
      printTitle: "Captive Consumption Note (CCN)",
      party: "party_or_text",
      conditions: ["good"],
      transit: "optional",
      hasReturnStatus: true,
      refLabel: null,
      refRequired: false,
      gatePass: true,
      permission: "stores.captive",
    },
    discard: {
      kind: "discard",
      label: "Material Discard",
      title: "Material Discard",
      prefix: "SMD",
      printCode: "MDN",
      printTitle: "Material Discard Note (MDN)",
      party: "text",
      conditions: ["damage"],
      transit: "optional",
      hasReturnStatus: true,
      refLabel: "Discard Instruction Ref. No.",
      refRequired: true,
      gatePass: true,
      permission: "stores.discard",
    },
  };

export const storesOutbound = (k: StoresOutboundKind) => STORES_OUTBOUND[k];

/** The Issue list's pills. Captive Consumption and Discard have lists of their own. */
export const STORES_ISSUE_GROUPS = [
  { value: "eindent", label: "Internal – e-Indent", kinds: ["issue_eindent"] },
  {
    value: "physical",
    label: "Internal – Physical Indent",
    kinds: ["issue_physical"],
  },
  {
    value: "stock_transfer",
    label: "Stock Transfer",
    kinds: ["issue_stock_transfer"],
  },
  {
    value: "return_vendor",
    label: "Return to Vendor",
    kinds: ["return_vendor"],
  },
  { value: "captive", label: "Captive Consumption", kinds: ["captive"] },
  { value: "discard", label: "Material Discard", kinds: ["discard"] },
] as const satisfies readonly {
  value: string;
  label: string;
  kinds: readonly StoresOutboundKind[];
}[];
export type StoresOutboundGroup = (typeof STORES_ISSUE_GROUPS)[number]["value"];

/** The other in-store numbers: SLOC Updation, Cycle Inventory and the e-Indent itself. */
export const STORES_SLOC_PREFIX = "SSU";
export const STORES_COUNT_PREFIX = "SCI";
export const STORES_INDENT_PREFIX = "SIN";
/** A floor's leftover sent back on an e-Indent: SIR/0001/Y/D. */
export const STORES_INDENT_RETURN_PREFIX = "SIR";
export const STORES_OTHER_PREFIXES = [
  ...Object.values(STORES_OUTBOUND).map((k) => k.prefix),
  STORES_SLOC_PREFIX,
  STORES_COUNT_PREFIX,
  STORES_INDENT_PREFIX,
  STORES_INDENT_RETURN_PREFIX,
];

/** Does this outbound document go out through the gate? */
export function needsGatePass(
  kind: StoresOutboundKind,
  transitApplicable: boolean
): boolean {
  const cfg = storesOutbound(kind);
  if (!cfg.gatePass) return false;
  return cfg.transit === "required" || transitApplicable;
}

export const STORES_RETURN_STATUSES = [
  { value: "returnable", label: "Returnable" },
  { value: "not_returnable", label: "Not Returnable" },
] as const;
export type StoresReturnStatus =
  (typeof STORES_RETURN_STATUSES)[number]["value"];

/** A party typed in (Captive "Fill", Discard). */
export interface StoresPartyText {
  name?: string | null | undefined;
  address1?: string | null | undefined;
  address2?: string | null | undefined;
  city?: string | null | undefined;
  pincode?: string | null | undefined;
  state?: string | null | undefined;
  phone?: string | null | undefined;
}

export interface StoresPick {
  condition: StoresCondition;
  locationId: number;
  ups: number;
  qty: DecimalInput;
}

export interface StoresOutboundLineInput {
  itemId: number | null | undefined;
  /** "As per Indent" (e-Indent / Physical Indent); 0 elsewhere. */
  askedUps?: number | null | undefined;
  askedQty?: DecimalInput;
  picks: readonly StoresPick[];
}

export function pickTotals(picks: readonly StoresPick[]): {
  ups: number;
  qty: string;
} {
  let ups = 0;
  let qty = toDecimal(0);
  for (const p of picks) {
    ups += p.ups;
    qty = qty.plus(toDecimal(p.qty ?? 0));
  }
  return { ups, qty: qty.toFixed(3) };
}

export function storesOutboundLineProblems(
  kind: StoresOutboundKind,
  l: StoresOutboundLineInput
): string[] {
  const cfg = storesOutbound(kind);
  const out: string[] = [];
  if (!l.itemId) out.push("Stores Item");
  const picks = l.picks.filter((p) => p.ups > 0 || toDecimal(p.qty ?? 0).gt(0));
  if (!picks.length) out.push("pick the bins it comes from");
  for (const p of picks) {
    if (p.ups < 0 || toDecimal(p.qty ?? 0).lt(0))
      out.push("quantities cannot be negative");
    if (!cfg.conditions.includes(p.condition))
      out.push(
        cfg.conditions.length === 1 && cfg.conditions[0] === "damage"
          ? "a discard takes Damage stock only"
          : "this issue takes Good stock only"
      );
    if (p.ups > 0 !== toDecimal(p.qty ?? 0).gt(0))
      out.push("each bin needs both UPS and Quantity");
  }
  const seen = new Set<string>();
  for (const p of picks) {
    const k = `${p.condition}:${p.locationId}`;
    if (seen.has(k)) out.push("a bin appears twice");
    seen.add(k);
  }
  if (
    (kind === "issue_physical" || kind === "issue_eindent") &&
    (l.askedUps ?? 0) <= 0 &&
    toDecimal(l.askedQty ?? 0).lte(0)
  )
    out.push("Quantity as per Indent");
  return [...new Set(out)];
}

export interface StoresOutboundHeader {
  kind: StoresOutboundKind;
  indentId?: number | null | undefined;
  indentNo?: string | null | undefined;
  indentRaisedBy?: string | null | undefined;
  destFacilityId?: number | null | undefined;
  vendorId?: number | null | undefined;
  partyId?: number | null | undefined;
  partyText?: StoresPartyText | null | undefined;
  refNo?: string | null | undefined;
  returnStatus?: string | null | undefined;
  transitApplicable?: boolean | null | undefined;
  transit?: DispatchTransport | null | undefined;
}

const blank = (v: string | null | undefined) => !(v ?? "").trim();

export function storesOutboundHeaderMissing(h: StoresOutboundHeader): string[] {
  const cfg = storesOutbound(h.kind);
  const out: string[] = [];
  switch (cfg.party) {
    case "indent":
      if (!h.indentId) out.push("e-Indent");
      break;
    case "physical_indent":
      if (blank(h.indentNo)) out.push("Indent Number");
      if (blank(h.indentRaisedBy)) out.push("Physical Indent Raised by");
      break;
    case "plant":
      // One of our plants, or a Stock Transfer party that is not on NexSeed.
      if (!h.destFacilityId && !h.partyId) out.push("Party Name");
      break;
    case "vendor":
      if (!h.vendorId) out.push("Party Name");
      break;
    case "party_or_text":
      if (!h.partyId) out.push(...missingPartyText(h.partyText));
      break;
    case "text":
      out.push(...missingPartyText(h.partyText));
      break;
  }
  if (cfg.refRequired && blank(h.refNo)) out.push(cfg.refLabel ?? "Ref. No.");
  if (cfg.transit !== "none") {
    if (cfg.transit === "optional" && h.transitApplicable == null)
      out.push("Mode of Transit");
    else if (cfg.transit === "required" || h.transitApplicable)
      out.push(...missingDispatchTransport(h.transit ?? {}));
  }
  if (cfg.hasReturnStatus && blank(h.returnStatus)) out.push("Return Status");
  return out;
}

function missingPartyText(p: StoresPartyText | null | undefined): string[] {
  const out: string[] = [];
  if (blank(p?.name)) out.push("Party Name");
  if (blank(p?.address1)) out.push("Address 1");
  if (blank(p?.city)) out.push("City/Town/Village");
  if (blank(p?.state)) out.push("State");
  return out;
}

export function storesOutboundBlockers(
  h: StoresOutboundHeader,
  lines: readonly StoresOutboundLineInput[]
): string[] {
  const out = storesOutboundHeaderMissing(h);
  if (!lines.length) out.push("at least one item");
  lines.forEach((l, i) => {
    for (const p of storesOutboundLineProblems(h.kind, l))
      out.push(`Item ${i + 1}: ${p}`);
  });
  return out;
}

// ---- e-Indent --------------------------------------------------------------------------------

export const STORES_INDENT_TABS = ["pending", "issued", "cancelled"] as const;
export type StoresIndentTab = (typeof STORES_INDENT_TABS)[number];
export type StoresIndentStatus =
  "open" | "partly_issued" | "issued" | "cancelled";
export const STORES_INDENT_STATUS_LABEL: Record<StoresIndentStatus, string> = {
  open: "Pending",
  partly_issued: "Partly issued",
  issued: "Issued",
  cancelled: "Cancelled",
};
export const STORES_INDENT_TAB_LABEL: Record<StoresIndentTab, string> = {
  pending: "Pending",
  issued: "Issued",
  cancelled: "Cancelled",
};

/**
 * An indent line's status from what was asked and what has been issued against it. An issue may
 * give more than was asked (a whole roll for part of one, user 2026-10-30): that line is issued.
 */
export function indentStatusOf(
  lines: readonly { qty: DecimalInput; issuedQty: DecimalInput }[]
): Exclude<StoresIndentStatus, "cancelled"> {
  const any = lines.some((l) => toDecimal(l.issuedQty ?? 0).gt(0));
  const all = lines.every((l) =>
    toDecimal(l.issuedQty ?? 0).gte(toDecimal(l.qty ?? 0))
  );
  return all && lines.length ? "issued" : any ? "partly_issued" : "open";
}

// ---- Leftover return on an e-Indent ----------------------------------------------------------
//
// The floor raises it against an e-Indent it was issued on (an 80 kg ask filled by a 100 kg roll:
// 20 kg come back). It waits at the store as Pending until the store receives it on an Internal
// Return – Own arrival made from it, put away into Stores bins; Final Submit makes it Received. The
// raiser withdraws it, or the store declines it, while no arrival is being made from it.

export const STORES_INDENT_RETURN_TABS = [
  "pending",
  "received",
  "cancelled",
] as const;
export type StoresIndentReturnTab = (typeof STORES_INDENT_RETURN_TABS)[number];
export type StoresIndentReturnStatus = StoresIndentReturnTab;
export const STORES_INDENT_RETURN_STATUS_LABEL: Record<
  StoresIndentReturnStatus,
  string
> = {
  pending: "Pending at store",
  received: "Received",
  cancelled: "Cancelled",
};

/** What a floor may still send back on an indent line: what was issued, less earlier returns. */
export function indentReturnable(l: {
  issuedUps: number;
  issuedQty: DecimalInput;
  returnedUps: number;
  returnedQty: DecimalInput;
}): { ups: number; qty: string } {
  const qty = toDecimal(l.issuedQty ?? 0).minus(toDecimal(l.returnedQty ?? 0));
  return {
    ups: Math.max(0, l.issuedUps - l.returnedUps),
    qty: (qty.gt(0) ? qty : toDecimal(0)).toFixed(3),
  };
}

// ---- Cycle Inventory -------------------------------------------------------------------------

/** Counted less book: positive is excess found, negative a shortage. */
export function countVariance(l: {
  bookUps: number;
  bookQty: DecimalInput;
  countedUps: number | null | undefined;
  countedQty: DecimalInput;
}): { ups: number; qty: string } | null {
  if (l.countedUps == null || l.countedQty == null) return null;
  return {
    ups: l.countedUps - l.bookUps,
    qty: toDecimal(l.countedQty)
      .minus(toDecimal(l.bookQty ?? 0))
      .toFixed(3),
  };
}
